/**
 * useNotificationSeed
 *
 * Seeds the live notification store with durable history from the REST
 * endpoint. The store (which powers the bell badge and dropdown) is otherwise
 * only ever populated by Laravel Echo events that arrive while the tab is open,
 * so on a fresh load it would be empty even though `/notifications` has rows.
 *
 * This hook hydrates that store once per authenticated session. Live Echo
 * events then keep it fresh from there; the seed does not refetch on route
 * changes or when the dropdown opens.
 *
 * The request is never cancelled from the effect's cleanup. React runs mount
 * effects twice in development (StrictMode), and a cleanup-side "cancelled"
 * flag discarded the only response while the once-per-session guard stopped
 * the second run from asking again — the bell stayed empty on every load. The
 * session boundary already protects the store: `apiClient` rejects a response
 * whose session ended while it was in flight (`SessionEndedError`), so a
 * late response is either this account's, and safe to write, or never
 * resolves at all.
 */

import { useEffect, useRef } from 'react';

import { NOTIFICATION_CONSTANTS } from '@personal-trainer-app/shared';
import { notificationService } from '@personal-trainer-app/shared/src/services';

import { useNotificationStore } from '@/stores';

/**
 * @param enabled Whether the current session should be seeded (typically
 *   `isAuthenticated && !!user?.username`). When it flips back to false the
 *   seed re-arms so the next login pulls fresh history.
 */
export function useNotificationSeed(enabled: boolean): void {
  const hydrateNotifications = useNotificationStore(
    (s) => s.hydrateNotifications
  );
  const seededRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      // Re-arm so a subsequent login seeds again.
      seededRef.current = false;
      return;
    }

    if (!NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS) return;
    if (seededRef.current) return;

    seededRef.current = true;

    // Pull the recent history (for the dropdown) and the true unread total (so
    // the badge can show "99+" beyond what fits in the in-memory window) in
    // parallel. The page size is the API's own (it takes no `per_page`); the
    // unread total comes from the second request's meta — only the count is read.
    Promise.all([
      notificationService.getPaginated({ page: 1 }),
      notificationService.getPaginated({ page: 1, unread_only: true }),
    ])
      .then(([history, unread]) => {
        hydrateNotifications(history.data, unread.meta.total);
      })
      .catch((error) => {
        // Allow a retry on the next mount / login if the seed failed.
        seededRef.current = false;
        console.warn(
          '[useNotificationSeed] Failed to seed notifications:',
          error
        );
      });
  }, [enabled, hydrateNotifications]);
}

export default useNotificationSeed;
